import { normalizeLangKey } from '@src/utility/bcp47';

import { delayDubManifest, heardLeadIn, leadInDubDelaySec } from './leadIn';

import type { DubSource } from './DubDescriptionStore';
import type { DubVoiceMode } from './DubEngine';
import type { ClipSpan } from './DubPlacementService';
import type { DubOptions } from '@src/editor/models/Timeline';
import type { DubManifestDTO } from '@src/repositories/DubbingRepository';

/**
 * Is the dubbed voice heard on this clip, and which dub is it?
 *
 * The one gate for "what is heard follows what is read": the dub audio is
 * placed exactly when this answers, and the captions switch to the dub's own
 * timing exactly when this answers for the caption language. Two copies of this
 * test could disagree, and then the captions would follow a voice that is not
 * playing.
 *
 * Heard means: a voice position other than Original, a language chosen, that
 * language's description held in memory, and at least one kept stretch of the
 * recording on the clip.
 *
 * The original lead-in's delay is applied here, to the description itself, so
 * the voice and the dub-timed captions both come in late together.
 */
export interface HeardDub {
  /** The dub's language, as the clip's options name it. */
  language: string;
  manifest: DubManifestDTO;
  renditionUrls: Record<string, string>;
  /** The clip's kept stretches of the recording. */
  spans: ClipSpan[];
}

/**
 * A dub heard from outside the editor's own timeline: the story-entry review
 * plays the story's dub itself and draws its captions from an editor that has
 * no picture of the recording, so it says which dub is heard and over which
 * stretch.
 */
export interface ExternalHeardDub {
  language: string;
  spans: ClipSpan[];
}

export function resolveHeardDub(input: {
  options: DubOptions | null;
  source: DubSource | null;
  spans: ClipSpan[];
  /** When set, this is the dub heard, over its own stretch, whatever the
   * timeline's own options say. */
  external?: ExternalHeardDub | null;
}): HeardDub | null {
  const { options, source, spans, external } = input;
  if (external) {
    const described = source?.describe(external.language);
    if (!described || external.spans.length === 0) return null;
    return {
      language: external.language,
      manifest: described.manifest,
      renditionUrls: described.renditionUrls,
      spans: external.spans,
    };
  }
  if (!source || !options) return null;
  if (options.voiceMode === 'original') return null;
  const { language } = options;
  if (!language) return null;
  const described = source.describe(language);
  if (!described) return null;
  if (spans.length === 0) return null;
  const leadIn = heardLeadIn(options.voiceMode, options.leadIn);
  return {
    language,
    manifest: leadIn
      ? delayDubManifest(described.manifest, leadInDubDelaySec(leadIn))
      : described.manifest,
    renditionUrls: described.renditionUrls,
    spans,
  };
}

/** Which of a dubbed language's two caption sets the caption track shows. */
export type CaptionSetKind = 'source' | 'dub';

/**
 * The dub-timed set is shown when the dub is heard, the captions are in the
 * dub's language, and the voice position is Dubbed — or Both, on an editor that
 * asked for Both to follow the dub (only the review does). Everything else
 * shows the source-timed set, which is today's captions.
 */
export function selectCaptionBasis(
  captionLanguage: string | null | undefined,
  heard: HeardDub | null,
  voiceMode: DubVoiceMode | null | undefined,
  bothFollowsDub: boolean,
): CaptionSetKind {
  if (!heard || !captionLanguage) return 'source';
  if (normalizeLangKey(captionLanguage) !== normalizeLangKey(heard.language)) {
    return 'source';
  }
  if (voiceMode === 'dubbed') return 'dub';
  if (voiceMode === 'both' && bothFollowsDub) return 'dub';
  return 'source';
}
